import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import {
  useGetShop,
  getGetShopQueryKey,
  useUpdateShopSettings,
} from '@/api/generated/v2-shop/v2-shop'
import {
  useGetScriptTagStatus,
} from '@/api/generated/v2-scripttag/v2-scripttag'
import {
  useSetupMeta,
  useDisableMetaCapi,
} from '@/api/generated/v2-meta/v2-meta'
import type {
  ShopResponse as OrvalShopResponse,
  ScriptTagStatusResponse,
  ShopSettingsUpdateResponse,
} from '@/api/schemas'
import { SetupPage } from '@/features/setup/SetupPage'
import {
  CUSTOMER_DELAY_SECONDS,
  ViewContentDelayControl,
} from '@/components/ViewContentDelayControl'
import { CopyableId } from '@/components/guide/CopyableId'
import { GuideStep } from '@/components/guide/GuideStep'
import { GuideImage } from '@/components/guide/GuideImage'
import { TipBox } from '@/components/guide/TipBox'
import { Input } from '@/components/ui/Input'
import { MaskedField } from '@/components/ui/MaskedField'
import { SaveErrorBanner } from '@/components/ui/SaveErrorBanner'
import { SettingsSection } from '@/components/ui/SettingsSection'
import { auth } from '@/lib/auth'
import { useConfirm } from '@/hooks/useConfirm'

export const Route = createFileRoute('/_dashboard/setup/meta-capi')({
  component: MetaCapiSetupPage,
})

function MetaCapiSetupPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirm()
  const [saving, setSaving] = useState(false)

  const [pixelId, setPixelId] = useState('')
  const [accessToken, setAccessToken] = useState('')
  // 저장 실패 사유 — toast와 별개로 폼에 계속 떠 있는 영구 배너용
  const [saveError, setSaveError] = useState<string | null>(null)
  // 폼 초기화 완료 여부 (Shop 데이터 로드 후 1회)
  const [formInitialized, setFormInitialized] = useState(false)
  // View/Edit 모드 — 미연동 상태면 처음부터 edit 모드로 진입
  const [isEditing, setIsEditing] = useState(false)
  // edit 진입 시 첫 필드 focus 용
  const pixelIdInputRef = useRef<HTMLInputElement>(null)

  // --- Orval Query 훅: Shop 정보 조회 ---
  const { data: shopRaw, isLoading: loading } = useGetShop({
    query: { enabled: auth.isAuthenticated() },
  })
  const shop = (shopRaw ?? null) as OrvalShopResponse | null

  // --- Orval Query 훅: 스크립트 설치 상태 ---
  const { data: scriptRaw } = useGetScriptTagStatus({
    query: { enabled: auth.isAuthenticated() },
  })
  const scriptStatus = (scriptRaw ?? null) as ScriptTagStatusResponse | null
  const scriptInstalled = scriptStatus?.installed ?? null

  // --- Orval Mutation 훅: Meta 수동 설정 / CAPI 해제 ---
  const setupMetaMutation = useSetupMeta()
  const disableCapiMutation = useDisableMetaCapi()
  // ViewContent 지연 발화는 Shop 설정 API(PUT /shop/)로 저장한다 — 픽셀/토큰 저장
  // 경로(setupMeta)와 분리해서 지연만 단독으로 켜고 끌 수 있게 한다.
  const updateSettingsMutation = useUpdateShopSettings()

  // 인증 안 된 경우 리다이렉트
  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate({ to: '/' })
    }
  }, [navigate])

  // Shop 데이터 로드/리페치 시 최초 1회 폼 초기값 세팅
  useEffect(() => {
    if (!shop) return
    if (formInitialized) return
    // 최초 1회: 폼 초기값 + edit 모드 판정
    if (shop.meta_pixel_id) setPixelId(shop.meta_pixel_id)
    // 액세스 토큰은 보안상 서버에서 평문 응답을 내려주지 않는다.
    // 연동 완료 상태면 "변경 시에만 입력" placeholder 가 표시된다.
    setFormInitialized(true)
    // 미연동 상태면 edit 모드로 진입 (pixel_id + token 둘 다 있어야 연결됨)
    const connected = !!shop.meta_pixel_id && !!shop.meta_capi_token_set
    if (!connected) {
      setIsEditing(true)
    }
  }, [shop, formInitialized])

  // edit 모드 진입 시 첫 필드 focus
  useEffect(() => {
    if (isEditing) {
      // 다음 렌더 사이클 이후에 focus
      const t = setTimeout(() => pixelIdInputRef.current?.focus(), 0)
      return () => clearTimeout(t)
    }
  }, [isEditing])

  const isConnected = !!shop?.meta_pixel_id && !!shop?.meta_capi_token_set

  // 선행 조건: 카페24 스크립트가 설치되어 있어야 전환 API를 켤 수 있다.
  // (스크립트가 클라이언트 픽셀 fbp/fbc 쿠키를 만들고 이벤트 ID를 발급해 CAPI 중복제거의 기준이 됨)
  // 이미 연동된 상태에서 스크립트가 (일시적으로) 미설치로 잡히면 안내만 띄우고 편집은 막지 않는다.
  const scriptMissing = scriptInstalled === false
  const prerequisiteMessage = scriptMissing
    ? '카페24 스크립트를 먼저 설치해주세요. 스크립트가 설치되어야 전환 데이터를 안전하게 수집할 수 있어요.'
    : undefined
  // 미연동 상태에서 스크립트가 없으면 저장 자체를 막는다.
  const blockedByScript = scriptMissing && !isConnected

  const externalLink = shop?.meta_pixel_id
    ? `https://business.facebook.com/events_manager2/list/pixel/${shop.meta_pixel_id}/overview`
    : undefined

  // 변경 감지 — pixel_id 가 기존값과 다르거나, 토큰 필드에 새 값이 입력됐으면 dirty
  const savedPixelId = shop?.meta_pixel_id ?? ''
  const isDirty = pixelId !== savedPixelId || accessToken !== ''

  /** Meta CAPI 설정 저장 */
  const handleSave = async () => {
    setSaveError(null)
    if (!pixelId.trim()) {
      setSaveError('데이터 세트(픽셀) ID를 입력해 주세요.')
      toast.error('데이터 세트 ID를 입력해주세요.')
      return
    }
    // 미연동 상태에서는 토큰 필수. 연동 완료 상태에서는 빈 값이면 "변경 안 함"
    if (!isConnected && !accessToken.trim()) {
      setSaveError('전환 API 액세스 토큰을 입력해 주세요.')
      toast.error('액세스 토큰을 입력해주세요.')
      return
    }

    setSaving(true)
    try {
      const trimmedToken = accessToken.trim()
      await setupMetaMutation.mutateAsync({
        data: {
          pixel_id: pixelId.trim(),
          // 빈 값이면 토큰을 덮어쓰지 않도록 키 자체를 전달하지 않는다 (백엔드 partial update)
          ...(trimmedToken ? { capi_access_token: trimmedToken } : {}),
        },
      })
      setSaveError(null)
      toast.success('Meta 전환 API 설정이 저장되었어요.')
      // Shop 쿼리 무효화 → 자동 리페치
      await queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
      // 저장된 토큰은 응답에 평문이 없으므로 빈 값으로 리셋
      setAccessToken('')
      // 성공 시 view 모드로 복귀 (catch 에서는 edit 유지)
      setIsEditing(false)
    } catch (error: any) {
      setSaveError(
        error?.message ||
          '픽셀 ID와 액세스 토큰을 확인하고 다시 시도해 주세요.',
      )
      toast.error('저장에 실패했어요. 입력값을 확인해 주세요.')
    } finally {
      setSaving(false)
    }
  }

  /** ViewContent 지연 발화 저장 — 실패는 throw 해서 컨트롤이 토글을 롤백하게 한다 */
  const handleDelaySave = async (seconds: number) => {
    const result = await updateSettingsMutation.mutateAsync({
      data: { view_content_delay_seconds: seconds },
    })
    // 백엔드 ShopSettingsUpdateResponse 는 검증 실패도 200 + success:false 로 내려준다
    const response = result as unknown as ShopSettingsUpdateResponse
    if (!response.success) {
      throw new Error(response.message ?? '설정 저장에 실패했어요.')
    }
    // Shop 쿼리 무효화 → 저장된 값으로 리페치.
    // 여기서 throw 되면 저장은 됐는데 토글만 롤백되는 거짓 실패가 되므로 삼킨다.
    try {
      await queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
    } catch (err) {
      console.error('Failed to refetch shop after saving delay:', err)
    }
  }

  /** 연동 해제 */
  const handleDisconnect = async () => {
    const ok = await confirm('메타로 전환 이벤트를 더 이상 보내지 않아요. 입력한 데이터 세트(픽셀) ID와 토큰도 지워져요.', {
      title: '메타 전환 API 연동을 해제할까요?',
      confirmText: '연동 해제',
      variant: 'danger',
    })
    if (!ok) return

    setSaving(true)
    try {
      // 전용 해제 엔드포인트 사용 — pixel_id, access_token 명시적 clear
      await disableCapiMutation.mutateAsync()
      setPixelId('')
      setAccessToken('')
      toast.success('연동을 해제했어요')
      // Shop 쿼리 무효화 → 자동 리페치
      await queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
      // 미연동 상태로 돌아갔으므로 edit 모드로 전환
      setIsEditing(true)
    } catch {
      toast.error('연동을 해제하지 못했어요. 잠시 후 다시 시도해 주세요')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 min-h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-outline-variant" />
      </div>
    )
  }

  // ===== View 모드 콘텐츠 =====
  const viewContent = (
    <div className="space-y-4">
      {/* FBE(카페24 페이스북 채널) 픽셀 중복 경고는 고객 대시보드에서 제거됨 —
          연동 과정 중 일시적 중복이 반드시 생기고 자동 해소도 지연(3일)돼
          고객에게 혼란만 줘서, Admin 운영 점검(FBE 픽셀 중복 체크)에서만 다룬다. */}
      <MaskedField
        label="데이터 세트(픽셀) ID"
        value={shop?.meta_pixel_id}
        mode="plain"
      />
      <MaskedField
        label="전환 API 액세스 토큰"
        value={null}
        mode="secret"
        isSet={shop?.meta_capi_token_set ?? false}
      />
    </div>
  )

  // ===== Edit 모드 콘텐츠 =====
  const editContent = (
    <div className="space-y-4">
      {/* 저장 실패 시 사라지지 않는 배너 — 무엇을 고쳐야 하는지 계속 안내 */}
      <SaveErrorBanner message={saveError} />
      <Input
        ref={pixelIdInputRef}
        label="데이터 세트(픽셀) ID"
        value={pixelId}
        onChange={(e) => setPixelId(e.target.value)}
        placeholder="1234567890"
        disabled={saving}
      />
      <Input
        label="전환 API 액세스 토큰"
        type="password"
        value={accessToken}
        onChange={(e) => setAccessToken(e.target.value)}
        placeholder={isConnected ? '변경 시에만 입력' : 'EAAxxxxxxx...'}
        disabled={saving}
      />
    </div>
  )

  /** 설정 수정 시작 — 폼을 현재 shop 값으로 리셋 */
  const handleEdit = () => {
    setPixelId(shop?.meta_pixel_id || '')
    setAccessToken('')
    setSaveError(null)
    setIsEditing(true)
  }

  /** 수정 취소 — 폼 원복 + view 모드 복귀 */
  const handleCancel = () => {
    setPixelId(shop?.meta_pixel_id || '')
    setAccessToken('')
    setSaveError(null)
    setIsEditing(false)
  }

  return (
    <>
    <SetupPage
      title="메타 전환 API 연동"
      connected={isConnected}
      prerequisiteMessage={prerequisiteMessage}
      externalLink={externalLink}
      externalLinkLabel="메타 이벤트 관리자"
      guide={<MetaCapiGuide />}
      // 연동 해제 — 공통 틀 ⑤(맨 아래). 지연 발화 설정 아래에 와야 해서 SettingsSection 이 아니라 여기서 그린다
      disconnect={
        isConnected && !isEditing
          ? {
              description: '연동을 해제하면 메타로 전환 이벤트를 보내지 않아요.',
              onClick: handleDisconnect,
              loading: saving,
            }
          : undefined
      }
    >
      <SettingsSection
        isEditing={isEditing}
        onEdit={handleEdit}
        onCancel={handleCancel}
        onSave={handleSave}
        saving={saving}
        canSave={isDirty && !blockedByScript}
        canSaveDisabledReason={
          blockedByScript
            ? '카페24 스크립트를 먼저 설치해주세요'
            : '변경된 내용이 없어요'
        }
        viewContent={viewContent}
        editContent={editContent}
        saveLabel="검증 및 저장"
      />

      {/* ViewContent 지연 발화 (실험) — 픽셀이 설정된 뒤에야 의미가 있어서 그때만 노출.
          단 이미 켜져 있으면 CAPI 를 해제해도 계속 보여준다(끌 방법이 사라지지 않게).
          픽셀/토큰과 저장 경로가 달라 위 '설정 수정'과 독립적으로 즉시 저장된다. */}
      {(shop?.meta_pixel_id || (shop?.view_content_delay_seconds ?? 0) > 0) && (
        <div className="mt-6 pt-6 border-t border-border">
          <ViewContentDelayControl
            value={shop?.view_content_delay_seconds ?? 0}
            onSave={handleDelaySave}
            saving={updateSettingsMutation.isPending}
            // 고객사는 초를 고르지 않고 3초로만 켠다 (세밀 조정은 운영팀 어드민에서)
            fixedSeconds={CUSTOMER_DELAY_SECONDS}
            // 고객사 화면에서는 '실험' 표기를 뺀다 (어드민에는 그대로 남겨 성격을 알린다)
            label="ViewContent 지연 발화"
            // 운영팀이 다른 초로 설정해 둔 샵에서도 설명이 실제 동작과 맞도록 실효값을 쓴다
            description={(seconds) =>
              `상품 페이지에 ${seconds}초 이상 머문 방문자만 ViewContent(상품 조회)로 메타에 보내요.`
            }
            note="들어오자마자 나간 방문자를 빼고, 실제로 상품을 본 사람만 광고 신호로 보내기 위한 설정이에요. 대신 ViewContent 건수는 줄어요. 설정은 최대 1시간 뒤 반영돼요."
          />
        </div>
      )}
    </SetupPage>

    <ConfirmDialog />
    </>
  )
}

// ============================================
// 헬퍼 컴포넌트
// ============================================

/** 전환 API 설정 가이드 */
function MetaCapiGuide() {
  return (
    <div className="space-y-2">
      <div className="mb-6">
        <h3 className="text-base font-semibold text-on-surface mb-2">전환 API(CAPI)란?</h3>
        <p className="text-sm text-on-surface-variant">
          서버에서 직접 메타로 전환 이벤트를 전송해요.
          브라우저 픽셀만 쓰는 것보다 데이터 정확도가 높고, 광고 성과가 좋아져요.
        </p>
      </div>

      <GuideStep number={1} title="Meta 파트너 권한 부여 (선택)">
        <p>
          필요할 때 저희가 충분한 지원을 드릴 수 있도록 파트너 권한을 부여해주세요.
          <strong> 권한이 없어도 CAPI 이벤트 전송은 정상 동작해요.</strong>
        </p>
        <p className="mt-2">
          메타 비즈니스 설정 → 사용자 → 파트너로 이동해요.
        </p>
        <ol className="list-decimal list-inside space-y-1.5 text-sm mt-2">
          <li>
            Insight Wave를 파트너로 추가해요.
            {/* 진입 경로 분기 — 다른 연동에서 이미 파트너를 추가했을 수 있다 */}
            <ul className="list-disc list-outside pl-6 space-y-1.5 mt-1.5">
              <li>
                처음 연동하는 경우: <strong>추가</strong> → <strong>파트너에게 자산에 대한 액세스 권한 부여</strong> 선택
                → 파트너 비즈니스 ID에 <CopyableId value="1081832460400655" /> 입력 후 다음
              </li>
              <li>
                이미 Insight Wave가 파트너 목록에 있는 경우: 목록에서 <strong>Insight Wave</strong> 선택
                → <strong>자산 할당</strong> 버튼 클릭
              </li>
            </ul>
          </li>
          <li>자산 및 권한 할당에서 <strong>데이터 세트(픽셀)</strong> 선택 → <strong>전체 관리 권한</strong> 토글 켜기 → 자산 할당</li>
          <li>"Insight Wave에서 내 비즈니스의 대행사 역할을 함" 체크 후 다음 (안 뜨면 건너뛰기)</li>
        </ol>
        <TipBox type="info">
          권한 할당 화면에서 <strong>데이터 세트</strong>가 회색으로 비활성화돼 있다면, 메타 정책상 신규 비즈니스가 몇 주 동안 데이터 세트 자산 공유를 못 하는 상태예요.
          몇 주 뒤 데이터 세트가 활성화되면 그때 권한을 추가해주세요.
        </TipBox>
      </GuideStep>

      <GuideStep number={2} title="데이터 세트(픽셀) ID 확인">
        <p>
          메타 이벤트 관리자에서 현재 광고에 사용 중인 데이터 세트(픽셀)를 확인하고, ID를 복사해요.
        </p>
        <GuideImage src="/images/guide/meta-capi/04-dataset-id.png" alt="데이터 세트 ID 확인" />
        <TipBox type="warning">
          카페24 픽셀로 광고를 돌리고 있다면 카페24 픽셀의 ID를 사용해야 해요.
          다른 픽셀을 넣으면 전환 데이터가 광고에 반영되지 않아요.
        </TipBox>
        <p className="text-xs font-medium text-primary mt-2">→ '데이터 세트(픽셀) ID' 입력칸에 넣어 주세요.</p>
      </GuideStep>

      <GuideStep number={3} title="설정 탭에서 액세스 토큰 만들기">
        <p>
          이벤트 관리자에서 해당 데이터 세트를 선택하고, <strong>설정</strong> 탭으로 이동해요.
        </p>
        <GuideImage src="/images/guide/meta-capi/02-settings-tab.png" alt="설정 탭에서 액세스 토큰 만들기" />
        <p>
          아래로 스크롤하여 <strong>"직접 통합을 설정하세요"</strong> →
          <strong> "Dataset Quality API로 설정"</strong> 선택 →
          <strong> "액세스 토큰 만들기"</strong>를 클릭해요.
        </p>
      </GuideStep>

      <GuideStep number={4} title="데이터 세트 선택 후 토큰 생성">
        <p>사용할 데이터 세트(픽셀)를 선택해요.</p>
        <GuideImage src="/images/guide/meta-capi/01-select-dataset.png" alt="데이터 세트 선택" />
        {/* 이미 체크된 항목은 전환 API가 이미 설정된 데이터 세트라 해제가 안 된다 — 고객이 이걸 오류로 오해한다 */}
        <p>
          목록에 이미 체크되어 있고 해제되지 않는 항목은 이전에 전환 API가 설정된 데이터 세트예요.
          그대로 두고 진행하시면 돼요.
        </p>
        {/* 새 데이터 세트가 목록에 없는 원인은 대부분 자산 할당 누락 — 실제 문의로 반복 확인됨 */}
        <TipBox type="warning">
          새로 만든 데이터 세트가 목록에 보이지 않는다면 <strong>자산 할당</strong>이 안 된 상태예요.
          데이터 세트를 직접 만드셨더라도 본인 계정에 자동으로 할당되지는 않아요.
          <strong> 비즈니스 설정 → 데이터 소스 → 데이터 세트</strong>에서 해당 데이터 세트를 선택하고,
          사용자 추가로 본인 계정에 <strong>전체 관리 권한</strong>을 부여한 뒤 다시 시도해주세요.
        </TipBox>
        <p>토큰이 생성되면 복사해요.</p>
        <GuideImage src="/images/guide/meta-capi/03-copy-token.png" alt="토큰 복사" />
        <TipBox type="danger">
          액세스 토큰은 생성 시 한 번만 표시돼요. 반드시 복사해두세요!
        </TipBox>
        <p className="text-xs font-medium text-primary mt-2">→ '전환 API 액세스 토큰' 입력칸에 넣어 주세요.</p>
      </GuideStep>

      <GuideStep number={5} title="카페24 페이스북 채널 연동 해제">
        {/* 연동을 먼저 끝낸 뒤 해제해야 데이터 공백이 없다 — 순서가 중요해 마지막 단계에 둔다 */}
        <p>
          전환 API 연동을 마쳤으니, 마지막으로 카페24 페이스북 채널 연동을 해제해요.
          연동이 남아있으면 카페24와 자사몰 사관학교 솔루션이 같은 픽셀로 이벤트를 <strong>동시에 전송</strong>해서
          중복 집계가 발생하고, 광고 성과가 실제보다 부풀려져 보여요.
        </p>
        <TipBox type="info">
          해제하는 건 카페24 어드민의 '페이스북 채널'이에요. 메타 픽셀과 광고 계정은 그대로 유지되고,
          자사몰 사관학교 솔루션이 같은 픽셀 ID로 이벤트를 이어서 보내기 때문에 광고·타겟팅·전환 추적은 끊기지 않아요.
          전환 API 연동을 먼저 마쳤으니 지금 해제해도 데이터 공백은 없어요.
        </TipBox>
        <p className="mt-2">
          <strong>카페24 어드민 → 판매채널 → 페이스북 → 페이스북 채널 → 채널 설정</strong>에서
          하단의 <strong>"연결 해제하기"</strong>를 클릭해요.
        </p>
        <GuideImage src="/images/guide/meta-capi/00-cafe24-disconnect.png" alt="카페24 페이스북 채널 연동 해제" />
        <TipBox type="warning">
          페이스북 채널을 사용하지 않는 경우에도 연동이 남아있을 수 있어요. 반드시 확인해주세요.
        </TipBox>
      </GuideStep>

      {/* 자주 묻는 질문 — "픽셀이 끊기나요?"류 반복 문의를 상시 노출로 선제 안내 */}
      <div className="mt-6 pt-6 border-t border-outline-variant/40">
        <h3 className="text-base font-semibold text-on-surface mb-3">자주 묻는 질문</h3>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-on-surface">
              Q. 카페24 페이스북 채널을 해제하면 메타 픽셀 연결도 끊기나요?
            </p>
            <p className="text-sm text-on-surface-variant mt-1">
              아니요. 해제하는 건 카페24 어드민 안의 '페이스북 채널'이에요.
              메타 픽셀과 광고 계정은 그대로 유지되고, 자사몰 사관학교 솔루션이 <strong>같은 픽셀 ID</strong>로
              이벤트를 이어서 보내요.
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-on-surface">
              Q. 지금 광고를 집행 중인데 문제가 생기나요?
            </p>
            <p className="text-sm text-on-surface-variant mt-1">
              문제없어요. 픽셀 ID가 그대로라 광고 최적화·타겟팅·전환 추적이 끊기지 않아요.
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-on-surface">
              Q. 연동하는 동안 데이터가 빠지나요?
            </p>
            <p className="text-sm text-on-surface-variant mt-1">
              전환 API 연동을 완료하면 그 순간부터 발생하는 이벤트가 누락 없이 전송돼요.
              가이드 순서대로 <strong>전환 API 연동을 먼저 마친 뒤</strong> 카페24 페이스북 채널을 해제하면 데이터 공백이 없어요.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
